import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { createSubCategory, deleteSubCategory, fetchSubCategories, updateSubCategory } from './subCategoryAsyncThunk';
import { SubCategory } from '../../../types';
import { toast } from 'react-toastify';

// Request body for creating a subcategory
export interface CreateSubCategoryRequest {
    category: string;
    name: string[];
    translationKey?: string;
    nameTranslations?: Record<string, string>;
}

// Request body for updating a subcategory
export interface UpdateSubCategoryRequest {
    name: string;
    status: number;
    category: string;
    translationKey?: string;
    nameTranslations?: Record<string, string>;
}


interface SubCategoryState {
    data: SubCategory[];
    isModalOpen: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Fetching status
    isEdit: boolean;
    editingRecord: SubCategory | null;
    loading: boolean;
    error: string | null;
}

const initialState: SubCategoryState = {
    data: [],
    isModalOpen: false,
    isEdit: false,
    status: 'idle',
    editingRecord: null,
    loading: false,
    error: null,
};

// Slice
const subCategorySlice = createSlice({
    name: 'subCategories',
    initialState,
    reducers: {
        setModalOpen(state, action: PayloadAction<boolean>) {
            state.isModalOpen = action.payload;
        },
        setEdit(state, action: PayloadAction<boolean>) {
            state.isEdit = action.payload;
        },
        setEditingRecord(state, action: PayloadAction<SubCategory | null>) {
            state.editingRecord = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchSubCategories.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSubCategories.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchSubCategories.rejected, (state, action) => {
                toast.error(action.payload || 'Failed to fetch subcategories');
                state.status = 'failed';
                state.error = action.payload || 'Failed to fetch subcategories';
            })
            // Create
            .addCase(createSubCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createSubCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.data = [...state.data, ...action.payload];
            })
            .addCase(createSubCategory.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to create subcategory');
                state.error = action.payload || 'Failed to create subcategory';
            })
            // Update
            .addCase(updateSubCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSubCategory.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(updateSubCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update subcategory';
            })
            // Delete
            .addCase(deleteSubCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteSubCategory.fulfilled, (state, action) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                }
                state.loading = false;
            })
            .addCase(deleteSubCategory.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete subcategory');
                state.error = action.payload || 'Failed to update subcategory';
            });
    },
});

export const { setModalOpen, setEdit, setEditingRecord } = subCategorySlice.actions;

export const selectSubCategories = (state: RootState) => state.subCategories;

export default subCategorySlice.reducer;
